import {
  Currency,
  type CurrencyConstructor,
} from '@webapp/core/entities/external/Currency'
import {
  TravelStatuses,
  type TravelStatusEnum,
} from '../enums/TravelStatusEnum'
import {
  NextApproverChoice,
  type NextApproverChoiceConstructor,
} from '../NextApproverChoice'
import {
  TravelApprovalLog,
  type TravelApprovalLogConstructor,
} from '../TravelApprovalLog'
import {
  TravelDestination,
  type TravelDestinationConstructor,
} from '../TravelDestination'
import {
  type TravelCategoryReference,
  type TravelUserReference,
} from '../TravelRequestSummary'

export interface TravelRequestDetailConstructor {
  id: number
  uuid: string
  status: TravelStatusEnum
  travelBudget: number
  purpose: string
  details: Nullable<string>
  dateCreated: ISODateString
  dateLastModified: ISODateString
  canEdit: boolean
  canDelete: boolean
  canApprove: boolean
  currency: CurrencyConstructor
  requester: TravelUserReference
  // Observed v1 payload carried only `next_approver_id: null` (non-pending
  // request) — pending-payload source unverified, see feature-documentation Gaps
  nextApprover: Nullable<TravelUserReference>
  // Display refs may be absent on the observed v1 payload (only `department_id`
  // was present) — see feature-documentation Gaps; mapper resolves best source.
  primaryCategory: Nullable<TravelCategoryReference>
  secondaryCategory: Nullable<TravelCategoryReference>
  secondaryCategoryId: number // from `department_id`; required by the edit PUT payload
  destinations: TravelDestinationConstructor[]
  logs: TravelApprovalLogConstructor[]
  // Key absent from the observed v1 payload — mapper defaults to null/[]
  nextApproverChoices: Nullable<NextApproverChoiceConstructor[]>
}

export class TravelRequestDetail {
  readonly id: number
  readonly uuid: string
  readonly status: TravelStatusEnum
  readonly travelBudget: number
  readonly purpose: string
  readonly details: Nullable<string>
  readonly dateCreated: ISODateString
  readonly dateLastModified: ISODateString
  readonly canEdit: boolean
  readonly canDelete: boolean
  readonly canApprove: boolean
  readonly currency: Currency
  readonly requester: TravelUserReference
  readonly nextApprover: Nullable<TravelUserReference>
  readonly primaryCategory: Nullable<TravelCategoryReference>
  readonly secondaryCategory: Nullable<TravelCategoryReference>
  readonly secondaryCategoryId: number
  readonly destinations: TravelDestination[]
  readonly logs: TravelApprovalLog[]
  readonly nextApproverChoices: NextApproverChoice[]

  constructor(data: TravelRequestDetailConstructor) {
    this.id = data.id
    this.uuid = data.uuid
    this.status = data.status
    this.travelBudget = data.travelBudget
    this.purpose = data.purpose
    this.details = data.details
    this.dateCreated = data.dateCreated
    this.dateLastModified = data.dateLastModified
    this.canEdit = data.canEdit
    this.canDelete = data.canDelete
    this.canApprove = data.canApprove
    this.currency = new Currency(data.currency)
    this.requester = data.requester
    this.nextApprover = data.nextApprover
    this.primaryCategory = data.primaryCategory
    this.secondaryCategory = data.secondaryCategory
    this.secondaryCategoryId = data.secondaryCategoryId
    this.destinations = data.destinations.map(
      (destination) => new TravelDestination(destination)
    )
    this.logs = data.logs.map((log) => new TravelApprovalLog(log))
    this.nextApproverChoices = (data.nextApproverChoices ?? []).map(
      (choice) => new NextApproverChoice(choice)
    )
  }

  // Computed properties
  get isPending(): boolean {
    return this.status === TravelStatuses.PENDING
  }

  get isApproved(): boolean {
    return this.status === TravelStatuses.APPROVED
  }

  get isDenied(): boolean {
    return this.status === TravelStatuses.DENIED
  }

  get requesterFullName(): string {
    return `${this.requester.firstName} ${this.requester.lastName}`
  }

  get nextApproverFullName(): Nullable<string> {
    return this.nextApprover
      ? `${this.nextApprover.firstName} ${this.nextApprover.lastName}`
      : null
  }

  get hasDetails(): boolean {
    return this.details !== null && this.details.trim().length > 0
  }

  get hasLogs(): boolean {
    return this.logs.length > 0
  }

  // Approve/Deny panel visibility (BHV-044): approvable && pending && can_edit
  get isApprovable(): boolean {
    return this.canApprove && this.isPending && this.canEdit
  }

  // "FINAL APPROVAL!" emphasis when no further routing choices (BHV-044)
  get isFinalApproval(): boolean {
    return this.nextApproverChoices.length === 0
  }

  // Methods
  // Send-reminder icon shown only to the requester on a pending request (BHV-043)
  public isRequestedBy(userId: number): boolean {
    return this.requester.id === userId
  }
}
